import { AppState } from '../../../redux/reducers';
import { connect } from 'react-redux';
import {
  FontIcon,
  IBasePickerSuggestionsProps,
  ITag,
  MessageBarType,
  SearchBox,
  Spinner,
  TagPicker
} from '@fluentui/react';

// Store
import * as PractitionerStore from '../../../redux/middleware/PractitionerMiddleware';
import * as PractitionerSpecialtyAssociationStore from '../../../redux/middleware/PractitionerSpecialtyAssociationMiddleware';
import { Practitioner } from '../../../models/Entities/Practitioners/Practitioner';
import { ErrorType } from '../../../models/HttpError';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DefaultButton } from '@fluentui/react';
import { MessageComponent } from '../../../commons/component/MessageComponent';
import PractitionerAssociationExportFormComponent from '../../Protocols/Practitioners/PractitionerAssociationExportForm/PractitionerAssociationExport.Form.Component';

////i18Next
import { useTranslation } from 'react-i18next';

// styles
import './PractitionerList.style.css';

interface IDispatchProps {
  GetAllPractitioners: typeof PractitionerStore.actionCreators.GetAllPractitioners;
  GetSpecialtiesByPractitionerId: typeof PractitionerSpecialtyAssociationStore.actionCreators.GetSpecialtiesByPractitionerId;
}

interface IConnectedProps {
  practitioners: Practitioner[];
  isLoading: boolean;
  isLoadingSuccess: boolean;
  failOnLoading: boolean;
  error: ErrorType;
}

type IProps = IDispatchProps & IConnectedProps;

export const PractitionerListComponent = ({
  practitioners,
  isLoading,
  isLoadingSuccess,
  failOnLoading,
  error,
  GetAllPractitioners,
  GetSpecialtiesByPractitionerId
}: IProps) => {

  const [searchText, setSearchText] = useState('');
  const [selectedSpecialtyTags, setSelectedSpecialtyTags] = useState<ITag[]>([]);
  const [isExportFormOpen, setIsExportFormOpen] = useState<boolean>(false);
  const specialtyDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const filterSpecialtySuggestions = useCallback(
    (filterText: string, tagList?: ITag[]): Promise<ITag[]> => {
      if (filterText.trim().length < 3) return Promise.resolve([]);
      if (specialtyDebounceRef.current) clearTimeout(specialtyDebounceRef.current);
      return new Promise<ITag[]>((resolve) => {
        specialtyDebounceRef.current = setTimeout(() => {
          const existingKeys = new Set(tagList?.map((t) => t.key) ?? []);
          const seen = new Set<number>();
          const suggestions: ITag[] = [];
          practitioners?.forEach((p) => {
            p.SpecialtiesAssociated?.forEach((sa) => {
              if (
                sa.specialty?.name &&
                !seen.has(sa.id) &&
                !existingKeys.has(sa.id) &&
                sa.specialty.name.toLowerCase().includes(filterText.trim().toLowerCase())
              ) {
                seen.add(sa.id);
                suggestions.push({ key: sa.id, name: sa.specialty.name });
              }
            });
          });
          resolve(suggestions);
        }, 300);
      });
    },
    [practitioners]
  );

  const navigate = useNavigate();
  const [t] = useTranslation();

  const onViewPractitionerEvent = (practitioner: Practitioner) => {
    navigate('/practitioners/' + practitioner.userId);
  };

  const onSearch = (newValue: string) => {
    setSearchText(newValue);
  };

  useEffect(() => {
    GetAllPractitioners(1, 1000, '');
  }, []);

  useEffect(() => {
    if (isLoadingSuccess && practitioners?.length > 0) {
      practitioners.forEach((p) => GetSpecialtiesByPractitionerId(p.userId));
    }
  }, [isLoadingSuccess]);

  const filteredPractitioners = practitioners?.filter((item) =>
    (item.fullName.toLowerCase().includes(searchText.toLowerCase()) ||
      item.registrationNumber?.toString().includes(searchText)) &&
    (selectedSpecialtyTags.length === 0 ||
      selectedSpecialtyTags.every((tag) =>
        item.SpecialtiesAssociated?.some((sa) => sa.id === tag.key)
      ))
  ) ?? [];

  return (
    <>
      {/* ── Header controls ── */}
      <div className="prac-list__header">
        <SearchBox
          className="prac-list__search"
          placeholder={t('practitionerList.parameters.search')}
          underlined={false}
          onChange={(_, newValue) => onSearch(newValue || '')}
          onClear={() => onSearch('')}
        />
        {process.env.REACT_APP_PREVIEW_FEATURES === 'true' && (
          <div className="prac-list__specialty-wrap">
            <TagPicker
              selectedItems={selectedSpecialtyTags}
              onResolveSuggestions={filterSpecialtySuggestions}
              getTextFromItem={(item: ITag) => item.name}
              pickerSuggestionsProps={{
                suggestionsHeaderText: t('practitionerList.parameters.specialtySuggestionsHeader'),
                noResultsFoundText: t('practitionerList.parameters.specialtyNoResults')
              } as IBasePickerSuggestionsProps}
              onChange={(items?: ITag[]) => setSelectedSpecialtyTags(items ?? [])}
              inputProps={{
                placeholder: t('practitionerList.parameters.searchSpecialty'),
                autoComplete: 'off'
              }}
            />
          </div>
        )}
        <DefaultButton
          iconProps={{ iconName: 'ExcelDocument' }}
          title={t('practitionerAssociationExport.title')}
          ariaLabel={t('practitionerAssociationExport.title')}
          onClick={() => setIsExportFormOpen(true)}
        >
          Exportar visitas
        </DefaultButton>
      </div>

      <PractitionerAssociationExportFormComponent
        isOpen={isExportFormOpen}
        onClose={() => setIsExportFormOpen(false)}
      />

      {failOnLoading && (
        <MessageComponent
          message={t('practitionerList.parameters.errorMessage')}
          subMessage={error.ErrorMessage}
          type={MessageBarType.error}
          itemList={error.Errors}
        />
      )}

      {(searchText || selectedSpecialtyTags.length > 0) && (
        <MessageComponent
          message={
            t('practitionerList.parameters.filterPrefix') +
            [
              searchText ? `${t('practitionerList.parameters.filterDoctor')}: "${searchText}"` : '',
              selectedSpecialtyTags.length > 0
                ? `${t('practitionerList.parameters.filterSpecialties')}: ${selectedSpecialtyTags.map((tag) => tag.name).join(', ')}`
                : ''
            ]
              .filter(Boolean)
              .join(' - ')
          }
          type={MessageBarType.info}
        />
      )}

      {isLoading && (
        <div className="prac-list__loading">
          <Spinner
            label={t('practitionerList.loadingSucces.medicalProfessionals')}
            ariaLive="assertive"
            labelPosition="right"
          />
        </div>
      )}

      {isLoadingSuccess && filteredPractitioners.length === 0 && (
        <div className="prac-list__empty">
          <FontIcon iconName="UserOptional" className="prac-list__empty-icon" />
          <p>{t('practitionerList.loadingSucces.medicalProfessionals')}</p>
        </div>
      )}

      {isLoadingSuccess && filteredPractitioners.length > 0 && (
        <div className="prac-list__grid">
          {filteredPractitioners.map((item) => {
            const cardClass = item.active ? 'prac-card' : 'prac-card prac-card--inactive';
            return (
            <div key={item.userId} className="prac-card-wrapper">
              <button
                className={cardClass}
                onClick={() => onViewPractitionerEvent(item)}
              >
                <div className="prac-card__accent" />
                <div className="prac-card__body">

                  <div className="prac-card__header">
                    <div className="prac-card__name-group">
                      <FontIcon iconName="UserOptional" className="prac-card__user-icon" />
                      <span className="prac-card__name">{item.fullName.toUpperCase()}</span>
                    </div>
                    <span className={`prac-card__badge${item.active ? ' prac-card__badge--active' : ' prac-card__badge--inactive'}`}>
                      {item.active
                        ? t('practitionerList.loadingSucces.enable')
                        : t('practitionerList.loadingSucces.disabled')}
                    </span>
                  </div>

                  <div className="prac-card__registration">
                    <span className="prac-card__reg-label">{t('practitionerList.loadingSucces.registration')}:</span>
                    <strong>{item.registrationNumber}</strong>
                    {item.speciality && (
                      <span className="prac-card__specialty"> · {item.speciality}</span>
                    )}
                  </div>

                  {process.env.REACT_APP_PREVIEW_FEATURES === 'true' &&
                    item.SpecialtiesAssociated?.length > 0 && (
                      <div className="prac-card__tags">
                        {item.SpecialtiesAssociated.map((sa) => (
                          <span key={sa.id} className="prac-card__tag">{sa.specialty?.name}</span>
                        ))}
                      </div>
                    )}

                  <div className="prac-card__footer">
                    <FontIcon iconName="NavigateForward" className="prac-card__nav-icon" />
                    <span>{t('practitionerList.loadingSucces.information')}</span>
                  </div>

                </div>
              </button>
            </div>
            );
          })}
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    practitioners: state.Practitioners?.list,
    isLoading: state.Practitioners?.isLoadingAll,
    isLoadingSuccess: state.Practitioners?.successLoadingAll,
    failOnLoading: state.Practitioners?.failOnLoadingAll,
    error: state.Practitioners?.error
  };
};

const mapDispatchToProps = {
  ...PractitionerStore.actionCreators,
  ...PractitionerSpecialtyAssociationStore.actionCreators
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PractitionerListComponent as any);
